/**
 * 抽屉框管理器
 * 用于管理多个抽屉框的打开/关闭状态，避免重叠
 */
import { ref, inject, provide, type InjectionKey } from 'vue'

export interface DrawerManager {
  // 注册抽屉框
  registerDrawer: (id: string, closeCallback: () => void) => void
  // 注销抽屉框
  unregisterDrawer: (id: string) => void
  // 打开抽屉框（会自动关闭其他抽屉框）
  openDrawer: (id: string) => void
  // 关闭抽屉框
  closeDrawer: (id: string) => void
  // 检查抽屉框是否打开
  isDrawerOpen: (id: string) => boolean
}

const DRAWER_MANAGER_KEY: InjectionKey<DrawerManager> = Symbol('drawer-manager')

/**
 * 创建抽屉框管理器
 */
export function createDrawerManager(): DrawerManager {
  // 每个管理器实例有自己独立的抽屉框 Map
  const drawers = ref<Map<string, () => void>>(new Map())

  const registerDrawer = (id: string, closeCallback: () => void) => {
    drawers.value.set(id, closeCallback)
  }

  const unregisterDrawer = (id: string) => {
    drawers.value.delete(id)
  }

  const openDrawer = (id: string) => {
    // 关闭所有其他抽屉框
    drawers.value.forEach((closeCallback, drawerId) => {
      if (drawerId !== id) {
        closeCallback()
      }
    })
  }

  const closeDrawer = (id: string) => {
    const closeCallback = drawers.value.get(id)
    if (closeCallback) {
      closeCallback()
    }
  }

  const isDrawerOpen = (id: string) => {
    return drawers.value.has(id)
  }

  return {
    registerDrawer,
    unregisterDrawer,
    openDrawer,
    closeDrawer,
    isDrawerOpen
  }
}

/**
 * 提供抽屉框管理器
 */
export function provideDrawerManager(manager: DrawerManager) {
  provide(DRAWER_MANAGER_KEY, manager)
}

/**
 * 注入抽屉框管理器
 */
export function useDrawerManager(): DrawerManager | undefined {
  return inject(DRAWER_MANAGER_KEY)
}

